import { SoundButton } from "../../../../common/SoundButton/SoundButton";
import type {
    ApiCommerceChainJourney,
    ApiCommerceOffer,
} from "@bridge-applications/pb-types";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import countdownTimerIcon from "../../../../../assets/static/images/commerce/chain-offer/countdown-timer.webp";
import { haptics } from "../../../../../logic/helpers/haptics";
import {
    presentationTranslationKey,
    remainingCountdown,
    telegramStarsPrice,
} from "../../../../../logic/helpers/commerce";
import type { TelegramInvoiceStatus } from "../../../../../logic/helpers/telegram";
import { useChainOfferPresentation } from "../../../../../logic/hooks/useChainOfferPresentation";
import type { ChainOfferStep } from "../../../../../logic/helpers/commerceChainPresentation";
import { SimpleDialog } from "../../../../common/SimpleDialog/SimpleDialog";
import { ChainOfferBoard } from "./components/ChainOfferBoard/ChainOfferBoard";
import { ChainOfferCampaignHeader } from "./components/ChainOfferCampaignHeader/ChainOfferCampaignHeader";
import "./ChainOfferDialog.scss";

interface ChainOfferDialogProps {
    isOpen: boolean;
    journey: ApiCommerceChainJourney | null;
    isBusy: boolean;
    error: string | null;
    onClose: () => void;
    onClaim: (journey: ApiCommerceChainJourney) => Promise<unknown>;
    onPurchase: (
        offer: ApiCommerceOffer,
        journeyId: string,
    ) => Promise<TelegramInvoiceStatus>;
}

type DialogMotionState = "closed" | "opening" | "open" | "closing";

const DIALOG_ENTRY_DURATION_MS = 700;
const DIALOG_EXIT_DURATION_MS = 180;
const REDUCED_MOTION_DURATION_MS = 80;

function dialogMotionDelay(durationMs: number): number {
    if (
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
        return REDUCED_MOTION_DURATION_MS;
    }

    return durationMs;
}

function useNow(isOpen: boolean): number {
    const [now, setNow] = useState(Date.now);

    useEffect(() => {
        if (!isOpen) return;
        setNow(Date.now());
        const timerId = window.setInterval(() => setNow(Date.now()), 1_000);
        return () => window.clearInterval(timerId);
    }, [isOpen]);

    return now;
}

function useDialogMotionState(isOpen: boolean): {
    isDialogMountedOpen: boolean;
    dialogMotionState: DialogMotionState;
} {
    const [isDialogMountedOpen, setIsDialogMountedOpen] = useState(isOpen);
    const [dialogMotionState, setDialogMotionState] =
        useState<DialogMotionState>(isOpen ? "open" : "closed");

    useEffect(() => {
        let timerId: number | undefined;

        if (isOpen) {
            setIsDialogMountedOpen(true);
            setDialogMotionState("opening");
            timerId = window.setTimeout(() => {
                setDialogMotionState("open");
            }, dialogMotionDelay(DIALOG_ENTRY_DURATION_MS));
        } else if (isDialogMountedOpen) {
            setDialogMotionState("closing");
            timerId = window.setTimeout(() => {
                setIsDialogMountedOpen(false);
                setDialogMotionState("closed");
            }, dialogMotionDelay(DIALOG_EXIT_DURATION_MS));
        }

        return () => {
            if (timerId !== undefined) window.clearTimeout(timerId);
        };
    }, [isDialogMountedOpen, isOpen]);

    return { isDialogMountedOpen, dialogMotionState };
}

export function ChainOfferDialog({
    isOpen,
    journey,
    isBusy,
    error,
    onClose,
    onClaim,
    onPurchase,
}: ChainOfferDialogProps) {
    const { t, i18n } = useTranslation();
    const locale = i18n.resolvedLanguage ?? i18n.language ?? "en";
    const now = useNow(isOpen);
    const { isDialogMountedOpen, dialogMotionState } =
        useDialogMotionState(isOpen);
    const presentation = useChainOfferPresentation(journey, isOpen);
    const presentedJourney = presentation.journey;
    const titleKey = presentedJourney
        ? presentationTranslationKey(presentedJourney.presentation, "titleKey")
        : null;
    const descriptionKey = presentedJourney
        ? presentationTranslationKey(
              presentedJourney.presentation,
              "descriptionKey",
          )
        : null;
    const title =
        titleKey && i18n.exists(titleKey)
            ? t(titleKey)
            : t("commerce.chain.title");
    const description =
        descriptionKey && i18n.exists(descriptionKey)
            ? t(descriptionKey)
            : t("commerce.chain.description");
    const isExpired = presentedJourney
        ? Date.parse(presentedJourney.expiresAt) <= now
        : false;
    const boardHeader = presentedJourney?.presentation.boardHeader ?? null;
    // Take the dialog's initial focus so showModal() does not auto-focus the
    // close control and render its focus-visible ring on open.
    const bodyRef = useRef<HTMLElement>(null);
    const closeControl = (
        <SoundButton
            type="button"
            className="chain-offer-dialog__close"
            aria-label={t("common.closeDialog")}
            onClick={() => {
                haptics.impact("light");
                onClose();
            }}
        >
            {"\u00d7"}
        </SoundButton>
    );
    const countdownContent = presentedJourney ? (
        <p className="chain-offer-dialog__timer" dir="ltr">
            <img
                className="chain-offer-dialog__timer-icon"
                src={countdownTimerIcon}
                width={180}
                height={180}
                alt=""
                aria-hidden="true"
                draggable={false}
            />
            {remainingCountdown(presentedJourney.expiresAt, now)}
        </p>
    ) : null;

    const handleStepActivation = async (
        step: ChainOfferStep,
    ): Promise<void> => {
        if (
            !presentedJourney ||
            step.state !== "AVAILABLE" ||
            presentation.phase !== "idle" ||
            dialogMotionState !== "open" ||
            isBusy ||
            isExpired
        ) {
            return;
        }

        const currentPrice = telegramStarsPrice(step.offer);
        if (step.action === "PAID_PURCHASE" && currentPrice === null) return;

        presentation.beginAuthoritativeAction();
        haptics.impact("light");

        try {
            if (step.action === "FREE_CLAIM") {
                await onClaim(presentedJourney);
                haptics.notification("success");
                return;
            }

            const status = await onPurchase(
                step.offer,
                presentedJourney.journeyId,
            );
            if (status === "paid") {
                haptics.notification("success");
                return;
            }

            presentation.cancelAuthoritativeAction();
            if (status === "failed") haptics.notification("error");
        } catch {
            presentation.cancelAuthoritativeAction();
            haptics.notification("error");
        }
    };

    return (
        <SimpleDialog
            className="chain-offer-dialog"
            isOpen={isDialogMountedOpen}
            onClose={onClose}
            data-motion-state={dialogMotionState}
            data-board-phase={presentation.phase}
            data-has-board-header={boardHeader !== null}
            aria-label={title}
            aria-describedby="chain-offer-dialog-description"
            initialFocusRef={bodyRef}
        >
            {presentedJourney && (
                <section
                    ref={bodyRef}
                    className="chain-offer-dialog__body"
                    tabIndex={-1}
                >
                    <p
                        id="chain-offer-dialog-description"
                        className="chain-offer-dialog__description"
                    >
                        {description}
                    </p>

                    <div className="chain-offer-dialog__campaign">
                        {boardHeader ? (
                            <ChainOfferCampaignHeader
                                header={boardHeader}
                                locale={locale}
                                closeControl={closeControl}
                                countdownContent={countdownContent}
                            />
                        ) : (
                            <div className="chain-offer-dialog__fallback-controls">
                                {closeControl}
                                {countdownContent}
                            </div>
                        )}
                        <ChainOfferBoard
                            steps={presentation.visibleSteps}
                            incomingStep={presentation.incomingStep}
                            phase={presentation.phase}
                            locale={locale}
                            isBusy={isBusy || dialogMotionState !== "open"}
                            isExpired={isExpired}
                            totalStepCount={presentedJourney.steps.length}
                            onActivate={(step) =>
                                void handleStepActivation(step)
                            }
                        />
                    </div>

                    {(isExpired || error) && (
                        <div className="chain-offer-dialog__message">
                            {isExpired && (
                                <p role="status">
                                    {t("commerce.chain.expired")}
                                </p>
                            )}
                            {error && <p role="alert">{error}</p>}
                        </div>
                    )}
                </section>
            )}
        </SimpleDialog>
    );
}
